import React, { useEffect, useState } from "react";
import { Sparkles, X, MessageCircle } from "lucide-react";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { api, buildWaLink } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

export const QuickLead = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", interest: "" });
  const [loading, setLoading] = useState(false);

  // pulse hint after 4s on first visit
  const [hinted, setHinted] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setHinted(true), 4000);
    return () => clearTimeout(t);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", {
        name: form.name,
        phone: form.phone,
        package_title: form.interest || null,
        source: "quick-lead",
        lang,
      });
      toast.success(t.quick.success);
      // also open WhatsApp pre-filled so user gets instant conversation
      const msg =
        t.waMessages.consult +
        (form.name ? ` (${form.name})` : "") +
        (form.interest ? ` — ${form.interest}` : "");
      window.open(buildWaLink(msg), "_blank");
      setForm({ name: "", phone: "", interest: "" });
      setOpen(false);
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating trigger pill — bottom-left */}
      <button
        data-testid="quick-lead-trigger"
        onClick={() => setOpen(true)}
        className={`fixed bottom-6 left-6 z-40 inline-flex items-center gap-2 rounded-full bg-[#0C2230] px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-[0_12px_30px_-8px_rgba(12,34,48,0.6)] transition-all hover:scale-105 hover:bg-[#0a1c27] md:bottom-8 md:left-8 ${
          hinted ? "animate-quickpulse" : ""
        }`}
      >
        <Sparkles className="h-4 w-4 text-[#B7892F]" />
        {t.quick.pill}
      </button>

      {/* Slide-up panel */}
      {open && (
        <div
          data-testid="quick-lead-overlay"
          className="fixed inset-0 z-50 flex items-end justify-start bg-[#0C2230]/40 backdrop-blur-sm md:items-end md:justify-start"
          onClick={() => setOpen(false)}
        >
          <div
            data-testid="quick-lead-panel"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-t-3xl bg-[#FBFAF6] p-7 shadow-2xl md:m-8 md:rounded-3xl"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0E9A57]/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#0E9A57]">
                  <Sparkles className="h-3 w-3" />
                  {t.quick.pill}
                </span>
                <h3 className="mt-3 font-[Fraunces] text-2xl font-medium leading-tight text-[#0C2230] md:text-3xl">
                  {t.quick.title}
                </h3>
                <p className="mt-1 text-sm text-[#0C2230]/65">{t.quick.sub}</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="close"
                data-testid="quick-lead-close"
                className="rounded-full border border-[#0C2230]/15 p-1.5 text-[#0C2230]/60 hover:bg-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={submit} className="mt-5 flex flex-col gap-3">
              <Input
                required
                data-testid="quick-lead-name"
                value={form.name}
                onChange={set("name")}
                placeholder={t.quick.name}
                className="h-12 rounded-xl border-[#0C2230]/15 bg-white"
              />
              <Input
                required
                data-testid="quick-lead-phone"
                value={form.phone}
                onChange={set("phone")}
                placeholder={t.quick.phone}
                className="h-12 rounded-xl border-[#0C2230]/15 bg-white"
              />
              <Input
                data-testid="quick-lead-interest"
                value={form.interest}
                onChange={set("interest")}
                placeholder={t.quick.interest}
                className="h-12 rounded-xl border-[#0C2230]/15 bg-white"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="quick-lead-submit"
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#0E9A57] px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-[#0c8a4d] disabled:opacity-50"
              >
                <MessageCircle className="h-4 w-4" />
                {loading ? t.quick.sending : t.quick.submit}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickLead;
